import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Card,
  Typography,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import Spinner from '../Spinner';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { SCOPES } from 'common/permissions';
import { ENTITY_TYPE } from 'common/constants';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { useApi } from 'api/ApiProvider';
import { useDialog } from 'common/DialogProvider';
import type { InvoiceType } from 'axiosApi/models';
import InvoiceTypeFormDrawer from './InvoiceTypeFormDrawer';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

const InvoiceTypeList = () => {
  const api = useApi();
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();

  const [paginationModel, setPaginationModel] = usePaginationPreference('invoiceTypeManagement', 25);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'country', sort: 'asc' },
  ]);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<InvoiceType | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Data states
  const [data, setData] = useState<any>(null);
  const [countriesData, setCountriesData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  // Filter states
  const [nameFilter, setNameFilter] = useState('');
  const [nameFilterForApi, setNameFilterForApi] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  useEffect(() => {
    const trimmed = nameFilter.trim();
    if (trimmed.length < 3) {
      setNameFilterForApi('');
      return;
    }
    const timer = setTimeout(() => {
      setNameFilterForApi(trimmed);
    }, 1000);
    return () => clearTimeout(timer);
  }, [nameFilter]);

  // Fetch countries for filter
  useEffect(() => {
    const fetchCountries = async () => {
      if (!api) return;
      try {
        const response = await api.addressApi.apiVversionListCountriesGet('1');
        setCountriesData(response.data);
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };
    fetchCountries();
  }, [api]);

  const countries = useMemo(() => {
    return countriesData?.data?.queryResult || [];
  }, [countriesData]);

  // Fetch invoice types with server-side pagination, sorting, and filtering
  const fetchInvoiceTypes = useCallback(async () => {
    if (!api) return;

    setLoading(true);
    setError(null);

    try {
      const sortField = sortModel[0]?.field || 'country';
      const sortOrder = sortModel[0]?.sort || 'asc';

      const nameParam = nameFilterForApi?.trim() || undefined;
      const countryParam = countryFilter ? parseInt(countryFilter) : undefined;

      const currentPage = paginationModel.page;
      const currentPageSize = paginationModel.pageSize;

      const response = await api.invoiceApi.apiVversionInvoiceTypeGet(
        '1',
        currentPage + 1,
        currentPageSize,
        sortField,
        sortOrder,
        nameParam,
        countryParam
      );

      setData((response.data as any).data);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setData({ queryResult: [], totalRecords: 0 });
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [api, paginationModel.page, paginationModel.pageSize, sortModel, nameFilterForApi, countryFilter]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchInvoiceTypes();
  }, [fetchInvoiceTypes]);

  // Reset pagination to page 0 when filters change
  useEffect(() => {
    setPaginationModel({ ...paginationModel, page: 0 });
  }, [nameFilterForApi, countryFilter]);

  const handleClearFilters = useCallback(() => {
    setNameFilter('');
    setCountryFilter('');
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedInvoiceType(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((invoiceType: InvoiceType) => {
    setSelectedInvoiceType(invoiceType);
    setDrawerOpen(true);
  }, []);

  const handleDelete = useCallback(
    (id: number, invoiceTypeName: string) => {
      const dialogId = openDialog({
        title: intl.get('invoiceTypeManagement.deleteTitle'),
        message: intl.get('invoiceTypeManagement.confirmDelete', { name: invoiceTypeName }),
        type: 'confirm',
        confirmButton: {
          confirmLabel: intl.get('invoiceTypeManagement.delete'),
          handleConfirm: async () => {
            try {
              await api.invoiceApi.apiVversionInvoiceTypeIdDelete(id, '1');
              toast.success({ body: intl.get('invoiceTypeManagement.deleteSuccess') });
              fetchInvoiceTypes();
              closeDialog(dialogId);
            } catch (err) {
              toast.error({ body: intl.get('invoiceTypeManagement.deleteError') });
              closeDialog(dialogId);
            }
          },
        },
        cancelButton: {
          cancelLabel: intl.get('invoiceTypeManagement.cancel'),
          handleCancel: () => {
            closeDialog(dialogId);
          },
        },
      });
    },
    [api, toast, openDialog, closeDialog, fetchInvoiceTypes]
  );

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedInvoiceType(null);
  }, []);

  const handleSuccess = useCallback(() => {
    fetchInvoiceTypes();
    handleDrawerClose();
  }, [fetchInvoiceTypes, handleDrawerClose]);

  const columns = useMemo<GridColDef<InvoiceType>[]>(
    () => [
      {
        field: 'name',
        headerName: intl.get('invoiceTypeManagement.name'),
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'description',
        headerName: intl.get('invoiceTypeManagement.description'),
        flex: 2,
        minWidth: 250,
      },
      {
        field: 'country',
        headerName: intl.get('invoiceTypeManagement.country'),
        flex: 1,
        minWidth: 150,
        valueGetter: (value, row) => row.country?.name || '',
      },
      {
        field: 'actions',
        headerName: intl.get('invoiceTypeManagement.actions'),
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <DefaultActions
            row={params.row}
            entityType={ENTITY_TYPE.EMPLOYEE}
            onEdit={handleEdit}
            onDelete={(row) => handleDelete(row.id!, row.name || '')}
            editScopes={[SCOPES['invoice.type.edit']]}
            deleteScopes={[SCOPES['invoice.type.delete']]}
          />
        ),
      },
    ],
    [handleEdit, handleDelete]
  );

  if (error) {
    return (
      <Card sx={{ m: 2 }}>
        <Box sx={{ p: 3 }}>
          <Chip label={intl.get('invoiceTypeManagement.loadError')} color="error" />
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography color="primary" variant="h2" component="h2">
            {intl.get('invoiceTypeManagement.title')}
          </Typography>
          <PermissionsGate
            viewScopes={[SCOPES['invoice.type.add']]}
            editScopes={[SCOPES['invoice.type.add']]}
            viewRoles={[]}
            editRoles={[]}
          >
            <Button
              variant="contained"
              startIcon={<Icon icon="mdi:plus" />}
              onClick={handleCreate}
            >
              {intl.get('invoiceTypeManagement.create')}
            </Button>
          </PermissionsGate>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3, p: 2, bgcolor: 'background.neutral' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              label={intl.get('invoiceTypeManagement.filterByName')}
              placeholder={intl.get('invoiceTypeManagement.searchPlaceholder')}
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: <Icon icon="mdi:magnify" style={{ marginRight: 8, color: '#666' }} />,
                },
              }}
            />
            <TextField
              fullWidth
              select
              size="small"
              label={intl.get('invoiceTypeManagement.filterByCountry')}
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <MenuItem value="">{intl.get('invoiceTypeManagement.allCountries')}</MenuItem>
              {countries.map((country: any) => (
                <MenuItem key={country.id} value={country.id}>
                  {intl.get(country.labelKey ?? country.label ?? '')}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<Icon icon="mdi:filter-off" />}
              sx={{ minWidth: 160 }}
            >
              {intl.get('invoiceTypeManagement.clearFilters')}
            </Button>
          </Stack>
        </Card>

        <Box sx={{ width: '100%', display: 'flex' }}>
          {loading ? <Spinner /> : (
          <DataGrid
            rows={data?.queryResult || []}
            columns={columns}
            getRowId={(row) => row.id || 0}
            rowCount={data?.totalRecords || 0}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
              }}
            />
          )}
        </Box>

        <InvoiceTypeFormDrawer
          open={drawerOpen}
          invoiceType={selectedInvoiceType}
          onClose={handleDrawerClose}
          onSuccess={handleSuccess}
          countries={countries}
        />
      </Box>
    </Card>
  );
};

export default InvoiceTypeList;
